/*
  TodayDashboardPanels — actionable panels for the "why did I open the
  app?" surface, per the UI/UX spec. Renders four cards side-by-side
  on desktop, stacking on mobile:

    * Today's appointments
    * My pending actions
    * Overdue enquiries
    * Onboarding forms submitted in the last 7 days

  All four counts + first-N items come from a single GET
  /api/dashboard/today call so the surface is fast.
*/
import { useCallback, useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useNavigate } from "react-router-dom";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import {
  Loader2, CalendarDays, ClipboardCheck, MessageCircle, FileText,
  ArrowRight, RefreshCw, CalendarClock,
} from "lucide-react";
import { cn } from "@/lib/utils";
import { FollowUpRows } from "@/components/enquiries/MyFollowUps";

function fmtTime(iso) {
  if (!iso) return "";
  try {
    return new Date(iso).toLocaleTimeString([], {
      hour: "numeric", minute: "2-digit",
    });
  } catch { return ""; }
}


export default function TodayDashboardPanels() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get("/dashboard/today");
      setData(r.data);
    } catch {
      // silent — the sync banner covers the "backend down" story
    } finally { setLoading(false); }
  }, []);

  useEffect(() => {
    load();
    const id = setInterval(load, 120_000);  // 2 min heartbeat
    return () => clearInterval(id);
  }, [load]);

  if (loading && !data) {
    return (
      <div className="text-sm text-zinc-500 py-6 flex items-center gap-2">
        <Loader2 className="h-4 w-4 animate-spin" /> Loading today's snapshot…
      </div>
    );
  }
  if (!data) return null;

  const appts = data.today_appointments || {};
  const pending = data.pending_actions || {};
  const enq = data.overdue_enquiries || {};
  const forms = data.forms_awaiting_review || {};
  const follow = data.my_follow_ups || {};

  return (
    <div className="space-y-3" data-testid="today-dashboard-panels">
      <div className="flex items-center gap-2">
        <h2 className="text-lg font-semibold text-zinc-950 tracking-tight">Today</h2>
        <div className="flex-1" />
        <Button size="sm" variant="ghost" onClick={load} className="h-7 px-2"
                data-testid="today-refresh">
          <RefreshCw className="h-3.5 w-3.5" />
        </Button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <Panel
          testId="panel-my-follow-ups"
          icon={CalendarClock} accent="rose"
          title="My follow-ups today"
          count={follow.count || 0}
          empty="No follow-ups due today."
          actionLabel="Open Follow-ups due"
          onAction={() => navigate("/enquiries?view=follow_ups")}
        >
          {(follow.items || []).length > 0 && <FollowUpRows items={follow.items} />}
        </Panel>

        <Panel
          testId="panel-today-appointments"
          icon={CalendarDays} accent="indigo"
          title="Today's appointments"
          count={appts.count || 0}
          empty={appts.available === false
            ? (appts.reason || "Acuity unavailable.")
            : "No appointments today."}
          actionLabel="View all"
          onAction={() => navigate("/appointments")}
        >
          {(appts.items || []).slice(0, 5).map((a) => (
            <li key={a.id}
                className="flex items-center gap-2 py-1 text-xs cursor-pointer hover:bg-zinc-50 rounded px-1 -mx-1"
                onClick={() => a.client_email && navigate(`/clients/${encodeURIComponent(a.client_email)}`)}
                data-testid={`panel-appt-${a.id}`}
            >
              <span className="font-mono-num text-zinc-500 tabular-nums w-14 flex-shrink-0">
                {fmtTime(a.time)}
              </span>
              <span className="truncate flex-1">
                <b className="text-zinc-900">{a.client_name || "(no name)"}</b>
                {a.type && <span className="text-zinc-500"> · {a.type}</span>}
              </span>
            </li>
          ))}
        </Panel>

        <Panel
          testId="panel-pending-actions"
          icon={ClipboardCheck} accent="emerald"
          title="My pending actions"
          count={pending.count || 0}
          empty="Nothing on your plate right now."
          actionLabel="Open Tasks"
          onAction={() => navigate("/tasks")}
        >
          {(pending.items || []).slice(0, 5).map((t) => (
            <li key={t.id}
                className="flex items-center gap-2 py-1 text-xs cursor-pointer hover:bg-zinc-50 rounded px-1 -mx-1"
                onClick={() => navigate("/tasks")}
                data-testid={`panel-task-${t.id}`}
            >
              <span
                className={cn(
                  "h-1.5 w-1.5 rounded-full flex-shrink-0",
                  t.priority === "high" ? "bg-red-500"
                                        : t.priority === "low" ? "bg-zinc-300"
                                                                 : "bg-amber-500",
                )}
              />
              <span className="truncate flex-1">
                <b className="text-zinc-900">{t.title}</b>
                {t.client_email && <span className="text-zinc-500"> · {t.client_email}</span>}
              </span>
              <span className="text-zinc-500 flex-shrink-0">{t.due_date}</span>
            </li>
          ))}
        </Panel>

        <Panel
          testId="panel-overdue-enquiries"
          icon={MessageCircle} accent="amber"
          title="Overdue enquiries"
          count={enq.count || 0}
          empty="No overdue enquiries."
          actionLabel="Open Enquiries"
          onAction={() => navigate("/enquiries")}
        >
          {(enq.items || []).slice(0, 5).map((e) => (
            <li key={e.id}
                className="flex items-center gap-2 py-1 text-xs cursor-pointer hover:bg-zinc-50 rounded px-1 -mx-1"
                onClick={() => navigate(`/enquiries?open=${e.id}`)}
                data-testid={`panel-enq-${e.id}`}
            >
              <span className="truncate flex-1">
                <b className="text-zinc-900">{e.name || "(unnamed)"}</b>
                {e.category && <span className="text-zinc-500"> · {e.category}</span>}
              </span>
              <span className="text-2xs text-amber-700">
                {e.email || e.phone || ""}
              </span>
            </li>
          ))}
        </Panel>

        <Panel
          testId="panel-forms-awaiting-review"
          icon={FileText} accent="sky"
          title="Onboarding forms · last 7 days"
          count={forms.count || 0}
          empty="No onboarding forms submitted this week."
          actionLabel="Open Onboarding"
          onAction={() => navigate("/intake-forms")}
        >
          {(forms.items || []).slice(0, 5).map((f) => (
            <li key={f.id}
                className="flex items-center gap-2 py-1 text-xs cursor-pointer hover:bg-zinc-50 rounded px-1 -mx-1"
                onClick={() => f.client_email
                  ? navigate(`/clients/${encodeURIComponent(f.client_email)}?tab=forms`)
                  : navigate("/intake-forms")}
                data-testid={`panel-form-${f.id}`}
            >
              <span className="truncate flex-1">
                <b className="text-zinc-900">
                  {f.client_name || f.client_email || "(unknown)"}
                </b>
                {f.form_name && <span className="text-zinc-500"> · {f.form_name}</span>}
              </span>
            </li>
          ))}
        </Panel>
      </div>
    </div>
  );
}


function Panel({ testId, icon: Icon, accent = "indigo", title, count,
                    empty, actionLabel, onAction, children }) {
  const empty_ = !children || (Array.isArray(children)
    ? children.filter(Boolean).length === 0 : false);
  const accentMap = {
    indigo: "text-indigo-700 bg-indigo-50 border-indigo-200",
    emerald: "text-emerald-700 bg-emerald-50 border-emerald-200",
    amber: "text-amber-700 bg-amber-50 border-amber-200",
    sky: "text-sky-700 bg-sky-50 border-sky-200",
    rose: "text-rose-700 bg-rose-50 border-rose-200",
  };
  return (
    <Card className="p-3" data-testid={testId}>
      <div className="flex items-center gap-2 mb-2">
        <span className={cn(
          "h-6 w-6 grid place-items-center rounded border",
          accentMap[accent] || accentMap.indigo,
        )}>
          <Icon className="h-3.5 w-3.5" />
        </span>
        <div className="text-sm font-semibold text-zinc-950 flex-1">{title}</div>
        <span className={cn(
          "text-xs font-semibold px-1.5 py-0.5 rounded border tabular-nums",
          (count || 0) === 0
            ? "border-zinc-200 text-zinc-500 bg-zinc-50"
            : accentMap[accent] || accentMap.indigo,
        )}>
          {count || 0}
        </span>
      </div>
      {empty_ ? (
        <div className="text-xs text-zinc-500 py-4 text-center">{empty}</div>
      ) : (
        <ul className="mb-1">{children}</ul>
      )}
      {onAction && (
        <button
          type="button"
          onClick={onAction}
          className="mt-1 text-xs text-zinc-600 hover:text-zinc-900 flex items-center gap-1"
          data-testid={`${testId}-action`}
        >
          {actionLabel} <ArrowRight className="h-3 w-3" />
        </button>
      )}
    </Card>
  );
}
